import type { DisplayCue } from "@expert/contentful";
import type { DeviceCues, LocationCues, SoftwareCues } from "@expert/shared-types";

import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import { deselectCue } from "@/sales-tools/detected-cues/services/deselectCue";
import { handleDetectedCue } from "@/sales-tools/detected-cues/services/handleDetectedCue";
import { setPreviousCues } from "@/sales-tools/detected-cues/services/setPreviousCues";

export type Location = {
    state?: string;
    city?: string;
    timezone?: string;
};

type DetectedCuesStore = {
    displayCuesBySession: Record<string, DisplayCue[]>;
    locationBySession: Record<string, Location>;

    // Display cues methods
    setDefaultPartnerCues: (sessionId: string, contentfulCues: DisplayCue[]) => DisplayCue[];
    processNewlyDetectedCue: (sessionId: string, newCues: (DeviceCues | SoftwareCues)[]) => void;
    processPreviouslyDetectedCues: (sessionId: string, previousCues: string[]) => void;
    deselectDetectedCue: (sessionId: string, cueToDeselect: string) => void;
    getDisplayCues: (sessionId: string) => DisplayCue[];

    // Location methods
    processLocationCues: (sessionId: string, locationCues: LocationCues[]) => void;
    getLocation: (sessionId: string) => Location | undefined;

    // Reset
    resetEvents: (sessionId: string) => void;
};

export const useDetectedCuesStore = createWithEqualityFn<DetectedCuesStore>()(
    persist(
        immer(
            devtools((set, get) => ({
                displayCuesBySession: {},
                locationBySession: {},

                setDefaultPartnerCues: (sessionId, contentfulCues) => {
                    const cues = contentfulCues ?? [];
                    set((state) => {
                        state.displayCuesBySession[sessionId] = cues;
                    });
                    return cues;
                },
                processNewlyDetectedCue: (sessionId, newCues) => {
                    set((state) => {
                        const current = state.displayCuesBySession[sessionId] ?? [];
                        state.displayCuesBySession[sessionId] = handleDetectedCue(current, newCues);
                    });
                },
                processPreviouslyDetectedCues: (sessionId, previousCues) => {
                    set((state) => {
                        const current = state.getDisplayCues(sessionId) ?? [];
                        state.displayCuesBySession[sessionId] = setPreviousCues(current, previousCues);
                    });
                },
                deselectDetectedCue: (sessionId, cueToDeselect) => {
                    set((state) => {
                        const current = state.displayCuesBySession[sessionId] ?? [];
                        state.displayCuesBySession[sessionId] = deselectCue(current, cueToDeselect);
                    });
                },
                getDisplayCues: (sessionId) => {
                    return get().displayCuesBySession[sessionId] ?? [];
                },

                // Location methods
                processLocationCues: (sessionId, locationCues) => {
                    const state = locationCues.find((cue) => cue.type === "LOCATION_STATE")?.name;
                    const city = locationCues.find((cue) => cue.type === "LOCATION_CITY")?.name;

                    // Only update if we have at least state or city
                    if (state || city) {
                        set((draft) => {
                            const current = draft.locationBySession[sessionId] ?? {};
                            draft.locationBySession[sessionId] = {
                                ...current,
                                ...(state ? { state } : {}),
                                ...(city ? { city } : {}),
                            };
                        });
                    }
                },
                getLocation: (sessionId) => {
                    const location = get().locationBySession[sessionId];
                    if (!location) return undefined;

                    const { city, state, timezone } = location;
                    if (!(city || state || timezone)) return undefined;

                    return location;
                },

                resetEvents: (sessionId) => {
                    set((state) => {
                        delete state.displayCuesBySession[sessionId];
                        delete state.locationBySession[sessionId];
                    });
                },
            })),
        ),
        { name: "detected-cues-store", skipHydration: true },
    ),
    shallow,
);
